import { useEffect, useState } from 'react';

/**
 * A custom hook that provides a debounced value of the input.
 *
 * This hook is useful for delaying operations that might be triggered by frequent state changes,
 * such as input field values, which can improve performance by reducing unnecessary computations.
 *
 * @param value - The value to be debounced (can be any primitive type: string, number, boolean, etc.)
 * @param delay - The delay in milliseconds before the value is updated (default: 500ms)
 * @returns The debounced value, which will only update after the specified delay has passed
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebounce(searchTerm, 300);
 *
 * // Use debouncedSearchTerm for API calls or expensive operations
 * useEffect(() => {
 *   if (debouncedSearchTerm) {
 *     searchAPI(debouncedSearchTerm);
 *   }
 * }, [debouncedSearchTerm]);
 * ```
 */
export default function useDebounce<T>(value: T, delay = 500): T
{
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() =>
	{
		const handler: NodeJS.Timeout = setTimeout(() =>
		{
			setDebouncedValue(value);
		}, delay);

		// Cancel the timeout if value changes (also on delay change or unmount)
		return () =>
		{
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
}
